import React, { Component } from 'react';
import { withApollo } from '@apollo/client/react/hoc';

import { Loader } from '@crehana/ui';

import { customLocalStorage } from '@crehana/utils';

import {
  METHODS,
  brazilState,
  inputValues,
} from 'Jsx/CheckoutV4/common/constants';
import I18N_STRINGS from 'Jsx/CheckoutV4/i18n/language';
import GET_EBANX_QUERY from 'Jsx/CheckoutV4/Graphql/Query/GetZipcodeData.payments.graphql';
import GET_ORDER_EXTRA_DATA_QUERY from 'Jsx/CheckoutV4/Graphql/Query/GetOrderExtraData.payments.graphql';
import VERIFY_BIN_PARTNERSHIP_MUTATION from 'Jsx/CheckoutV4/Graphql/Mutation/VerifyBINPartnership.payments.graphql';
import { CountryPrefixEnum, PaymentSources } from 'Jsx/CheckoutV4/types';
import { CheckoutContext } from 'Jsx/CheckoutV4/Context';
import PendingOnlinePayment from 'Jsx/CheckoutV4/shared/PendingOnlinePayment';

import getCreditCardNumber, {
  validateCreditCardNumber,
  getCreditCardBIN,
} from 'Jsx/CheckoutV4/utils/creditCard/getCreditCardNumber';
import getCreditCardExpiry, {
  validateCardExpiry,
} from 'Jsx/CheckoutV4/utils/creditCard/getCreditCardExpiry';
import getCreditCardCVC, {
  validateCreditCardCVC,
} from 'Jsx/CheckoutV4/utils/creditCard/getCreditCardCVC';
import getFullName, {
  validateFullName,
} from 'Jsx/CheckoutV4/utils/getFullName';
import getIdentityDocument, {
  validateIdentityDocument,
} from 'Jsx/CheckoutV4/utils/identityDocument/getIdentityDocument';
import getPhoneNumber, {
  validatePhoneNumber,
} from 'Jsx/CheckoutV4/utils/phoneNumber/getPhoneNumber';
import { DocumentTypeEnum } from 'Jsx/Types/Graphql/globals.payments';
import { StripeTokenResponse } from 'Jsx/CheckoutV4/types/StripeV2';
import { EbanxTokenResponse } from 'Jsx/CheckoutV4/types/Ebanx';

import Form from './components/Form';
import { CheckoutFormProps, FormStateType } from './types';
import FailedPaymentPopup from './components/FailedPaymentPopup/FailedPaymentPopup';

class CheckoutForm extends Component<CheckoutFormProps, FormStateType> {
  constructor(props: CheckoutFormProps) {
    super(props);
    this.state = {
      creditCardIssuer: undefined,
      creditCardNumber: '',
      creditCardExpiry: '',
      creditCardCVV: '',
      creditCardCVVDigits: 3,
      creditCardNumberErrorMessage: '',
      creditCardExpiryErrorMessage: '',
      creditCardCVVErrorMessage: '',
      isCreditCardNumberValid: false,
      isCreditCardExpiryValid: false,
      isCreditCardCVVValid: false,
      creditCardDigitsWithBlankSpaces: 14,
      paymentError: '',
      processing: false,
      previousState: 0,
      stripeToken: '',
      fullName: '',
      documentType: DocumentTypeEnum.CC,
      fullNameErrorMessage: '',
      isFullNameValid: false,
      phoneNumber: '',
      phoneNumberErrorMessage: '',
      isPhoneNumberValid: false,
      identityDocument: '',
      identityDocumentErrorMessage: '',
      isIdentityDocumentValid: false,
      userAddress: '',
      userAddressErrorMessage: '',
      isUserAddressValid: false,
      cityName: '',
      cityNameErrorMessage: '',
      isCityNameValid: false,
      provinceOrStateName: '',
      provinceOrStateNameErrorMessage: '',
      isProvinceOrStateNameValid: false,
      userZipCode: '',
      userZipCodeErrorMessage: '',
      isUserZipCodeValid: false,
      hideAddicionalInfo: true,
      hideCardDetails: true,
      creditCardNumberFocus: false,
      creditCardExpiryFocus: false,
      creditCardCVVFocus: false,
      paymentMutationStatus: null,
      checkedTerms: true,
      checkedNewsLetterTerms: false,
    };
  }

  componentDidMount() {
    const { client } = this.props;
    const { isGuestCheckout } = this.context;

    if (!isGuestCheckout) {
      // @ts-expect-error
      client
        .query({
          query: GET_ORDER_EXTRA_DATA_QUERY,
          context: { clientName: 'payments' },
        })
        .then(({ data }) => {
          if (!data) throw Error('');
          if (data.orderExtraData) {
            const { address, cep, city, document, phoneNumber, state } =
              data.orderExtraData;
            const findState = brazilState.find(
              zipData => zipData.value === state,
            );

            this.setState({
              identityDocument: document,
              isIdentityDocumentValid: !!document,
              userZipCode: cep,
              isUserZipCodeValid: !!cep,
              userAddress: address,
              isUserAddressValid: !!address,
              provinceOrStateName: findState || state,
              isProvinceOrStateNameValid: !!state,
              cityName: city,
              isCityNameValid: !!city,
              hideCardDetails: false,
              hideAddicionalInfo: false,
              phoneNumber,
              isPhoneNumberValid: !!phoneNumber,
            });
          }
        })
        .catch(err => console.log(err));
    }

    const { setPaymentFormValues } = this.context;

    // News letter terms are not mandatory
    setPaymentFormValues({
      checkedNewsLetterTerms: { isComplete: true, value: false },
    });
  }

  onChangeFullName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { fullName, fullNameErrorMessage, isFullNameValid } =
      getFullName(event);

    this.setState({
      fullName,
      fullNameErrorMessage,
      isFullNameValid,
    });
  };

  onBlurFullName = (event: React.FocusEvent<HTMLInputElement>) => {
    const { isFullNameValid, fullNameErrorMessage } = validateFullName({
      inputEvent: event,
      isCreditCardFullName: true,
    });

    this.setState({
      fullNameErrorMessage,
      isFullNameValid,
    });
  };

  onChangeCreditCardNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { setPaymentFormValues } = this.context;
    const {
      isCreditCardNumberValid,
      creditCardIssuer,
      creditCardNumberMask,
      creditCardDigitsWithBlankSpaces,
      creditCardNumberErrorMessage,
    } = getCreditCardNumber(event);

    this.setState({
      creditCardDigitsWithBlankSpaces,
      creditCardIssuer,
      creditCardNumber: creditCardNumberMask,
      isCreditCardNumberValid,
      creditCardNumberErrorMessage,
      creditCardExpiryFocus: isCreditCardNumberValid,
    });

    setPaymentFormValues({
      creditCardNumber: {
        value: creditCardNumberMask,
        isComplete: isCreditCardNumberValid,
        brand: creditCardIssuer,
      },
    });
  };

  onBlurCreditCardNumber = (event: React.FocusEvent<HTMLInputElement>) => {
    const { isValidPartnershipItem, checkoutProductDetail } = this.context;
    const {
      isCreditCardNumberValid,
      creditCardNumberErrorMessage,
      creditCardNumberMask,
    } = validateCreditCardNumber(event);

    if (
      isValidPartnershipItem &&
      creditCardNumberMask &&
      isCreditCardNumberValid
    ) {
      const { creditCardBin } = getCreditCardBIN({
        creditCardNumber: creditCardNumberMask,
      });

      this.verifyBINPartnershipMutation({
        bin: creditCardBin,
        itemId: checkoutProductDetail.originalId,
      });
    } else {
      this.setState({ isCreditCardNumberValid, creditCardNumberErrorMessage });
    }
  };

  onChageCreditCardExpiry = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { setPaymentFormValues } = this.context;
    const {
      creditCardExpiry,
      creditCardExpiryErrorMessage,
      isCreditCardExpiryValid,
    } = getCreditCardExpiry(event);

    this.setState({
      creditCardExpiry,
      creditCardExpiryErrorMessage,
      isCreditCardExpiryValid,
      creditCardCVVFocus: isCreditCardExpiryValid,
    });

    setPaymentFormValues({
      creditCardExpiry: {
        value: creditCardExpiry,
        isComplete: isCreditCardExpiryValid,
      },
    });
  };

  onBlurCreditCardExpiry = (event: React.FocusEvent<HTMLInputElement>) => {
    const { creditCardExpiryErrorMessage, isCreditCardExpiryValid } =
      validateCardExpiry(event);

    this.setState({ creditCardExpiryErrorMessage, isCreditCardExpiryValid });
  };

  onChangeCreditCardCVV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { creditCardIssuer } = this.state;
    const { setPaymentFormValues } = this.context;
    const {
      creditCardCVV,
      isCreditCardCVVValid,
      creditCardCVVErrorMessage,
      creditCardCVVDigits,
    } = getCreditCardCVC({ eventInput: event, creditCardIssuer });

    this.setState({
      creditCardCVV,
      isCreditCardCVVValid,
      creditCardCVVErrorMessage,
      creditCardCVVDigits,
    });
    setPaymentFormValues({
      creditCardCVV: {
        value: creditCardCVV,
        isComplete: isCreditCardCVVValid,
      },
    });
  };

  onBlurCVV = (event: React.FocusEvent<HTMLInputElement>) => {
    const { creditCardIssuer } = this.state;
    const {
      creditCardCVVErrorMessage,
      isCreditCardCVVValid,
      creditCardCVVDigits,
    } = validateCreditCardCVC({ eventInput: event, creditCardIssuer });

    this.setState({
      creditCardCVVErrorMessage,
      isCreditCardCVVValid,
      creditCardCVVDigits,
    });
  };

  onChangeIdentityDocument = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { countryPrefix, setPaymentFormValues } = this.context;
    const { documentType } = this.state;
    const {
      identityDocument,
      isIdentityDocumentValid,
      identityDocumentErrorMessage,
    } = getIdentityDocument({
      country: countryPrefix,
      inputEvent: event,
      documentType,
    });

    this.setState({
      identityDocument,
      identityDocumentErrorMessage,
      isIdentityDocumentValid,
    });
    setPaymentFormValues({
      identityDocument: {
        value: identityDocument,
        type: documentType,
        isComplete: isIdentityDocumentValid,
      },
    });
  };

  onChangeDocumentType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { setPaymentFormValues } = this.context;
    const documentType = event.target.value as DocumentTypeEnum;

    this.setState({
      documentType,
      identityDocument: '',
      isIdentityDocumentValid: false,
    });
    setPaymentFormValues({
      identityDocument: {
        value: documentType,
        type: '',
        isComplete: false,
      },
    });
  };

  onBlurIdentityDocument = (event: React.FocusEvent<HTMLInputElement>) => {
    const { countryPrefix } = this.context;
    const { isIdentityDocumentValid, identityDocumentErrorMessage } =
      validateIdentityDocument({
        inputEvent: event,
        country: countryPrefix,
      });

    this.setState({ isIdentityDocumentValid, identityDocumentErrorMessage });
  };

  onChangeUserAddress = (event: React.ChangeEvent<HTMLInputElement>) => {
    const userAddress = event.target.value;

    this.setState({
      userAddress,
      userAddressErrorMessage: '',
      isUserAddressValid: !!(userAddress && userAddress.length > 1),
    });
  };

  onBlurUserAddress = () => {
    if (this.state.userAddress === '') {
      this.setState({
        userAddressErrorMessage: I18N_STRINGS.REQUIRED_USER_ADDRESS,
      });
    }
  };

  onChangeCityName = (event: React.ChangeEvent<HTMLInputElement>) => {
    let cityName = event.target.value;

    cityName = cityName.replace(/^\s/, '');
    this.setState({
      cityName,
      cityNameErrorMessage: '',
      isCityNameValid: !!cityName,
    });
  };

  onBlurCityName = () => {
    if (this.state.cityName === '') {
      this.setState({
        cityNameErrorMessage: I18N_STRINGS.REQUIRED_CITY_NAME,
      });
    }
  };

  // @ts-expect-error
  onChangeProvinceOrStateName = stateSelected => {
    const value =
      stateSelected && stateSelected.target
        ? stateSelected.target.value
        : stateSelected;

    this.setState({
      provinceOrStateName: value,
      provinceOrStateNameErrorMessage: '',
      isProvinceOrStateNameValid: !!value,
    });
  };

  onBlurProvinceOrStateName = () => {
    const { provinceOrStateName } = this.state;

    if (!provinceOrStateName) {
      this.setState({
        provinceOrStateNameErrorMessage: I18N_STRINGS.REQUIRED_STATE_NAME,
        isProvinceOrStateNameValid: false,
      });
    }
  };

  onChangeUserZipCode = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { selectedPaymentMethod } = this.context;
    const { defaultValue, value } = event.target;
    let userZipCode = value;
    let isUserZipCodeValid = false;

    if (selectedPaymentMethod.title === METHODS.EBANX_CARD) {
      const zipRegex = /\d{5}[-]\d{3}/;

      userZipCode = userZipCode.replace(/[a-zA-z]|\s/g, '');
      userZipCode = userZipCode.replace(
        /^(\d{5})(\d{3})$/g,
        (_, p1, p2) => `${p1}-${p2}`,
      );

      if (
        defaultValue.length < userZipCode.length &&
        userZipCode.length === 5
      ) {
        userZipCode = userZipCode.replace(/(\d{5})/, '$&-');
      }
      isUserZipCodeValid = zipRegex.test(userZipCode);
    } else if (selectedPaymentMethod.title === METHODS.EBANX_CARD_AR) {
      const zipRegex = /[a-zA-Z]{1}\d{4}/;

      userZipCode = value;
      isUserZipCodeValid = zipRegex.test(value);
      this.setState({
        userZipCodeErrorMessage: '',
      });
    }

    this.setState({
      userZipCode,
      userZipCodeErrorMessage: '',
    });
    if (selectedPaymentMethod.title === METHODS.EBANX_CARD_AR) {
      this.setState({
        hideAddicionalInfo: false,
        isUserZipCodeValid,
        userZipCodeErrorMessage: '',
      });
    } else if (
      selectedPaymentMethod.title === METHODS.EBANX_CARD &&
      isUserZipCodeValid
    ) {
      this.props.client
        ?.query({
          query: GET_EBANX_QUERY,
          variables: { zipcode: userZipCode },
          context: { clientName: 'payments' },
        })
        .then(({ data }) => {
          if (!data) throw Error('');
          const { state, city, address, status } = data?.zipcodeData;
          const findState = state
            ? brazilState.find(stateData => stateData.value === state)
            : { value: '', label: '' };

          this.setState({
            hideAddicionalInfo: false,
            userAddress: address,
            isUserAddressValid: !!address,
            userAddressErrorMessage: '',
            cityName: city,
            isCityNameValid: !!city,
            cityNameErrorMessage: '',
            // @ts-expect-error
            provinceOrStateName: state ? findState : undefined,
            isProvinceOrStateNameValid: !!state,
            isUserZipCodeValid: status,
            userZipCodeErrorMessage: status
              ? ''
              : I18N_STRINGS.INVALID_ZIP_CODE,
          });
        })
        .catch(() =>
          this.setState({
            isUserZipCodeValid: false,
            hideAddicionalInfo: true,
            userZipCodeErrorMessage: I18N_STRINGS.INVALID_ZIP_CODE,
          }),
        );
    } else {
      this.setState({ hideAddicionalInfo: true, isUserZipCodeValid });
    }
  };

  onBlurUserZipCode = () => {
    const { selectedPaymentMethod } = this.context;
    const { userZipCode, isUserZipCodeValid } = this.state;
    let userZipCodeErrorMessage = I18N_STRINGS.INVALID_ZIP_CODE;
    let hasValidDigits = false;

    if (userZipCode === '') {
      this.setState({
        userZipCodeErrorMessage: I18N_STRINGS.REQUIRED_ZIP_CODE,
      });
    } else if (selectedPaymentMethod.title === METHODS.EBANX_CARD) {
      hasValidDigits = userZipCode.length < 9;
      userZipCodeErrorMessage = hasValidDigits
        ? I18N_STRINGS.INVALID_ZIP_CODE
        : '';
    } else if (selectedPaymentMethod.title === METHODS.EBANX_CARD_AR) {
      const zipRegex = /[a-zA-Z]{1}\d{4}/;

      hasValidDigits = zipRegex.test(userZipCode);
      userZipCodeErrorMessage = !hasValidDigits
        ? I18N_STRINGS.INVALID_ZIP_CODE
        : '';
    }
    if (!isUserZipCodeValid) {
      userZipCodeErrorMessage = I18N_STRINGS.INVALID_ZIP_CODE;
    }

    this.setState({
      userZipCodeErrorMessage,
    });
  };

  onChangePhoneNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { countryPrefix } = this.context;

    const { phoneNumber, isPhoneNumberValid, phoneNumberErrorMessage } =
      getPhoneNumber({
        country: countryPrefix,
        event,
      });

    this.setState({
      phoneNumber,
      phoneNumberErrorMessage,
      isPhoneNumberValid,
      hideCardDetails: false,
    });
  };

  onBlurPhoneNumber = (event: React.FocusEvent<HTMLInputElement>) => {
    const { countryPrefix } = this.context;
    const { isPhoneNumberValid, phoneNumberErrorMessage } = validatePhoneNumber(
      { country: countryPrefix, event },
    );

    this.setState({ isPhoneNumberValid, phoneNumberErrorMessage });
  };

  validateFirstStepsFields = (): boolean => {
    const {
      isPhoneNumberValid,
      isIdentityDocumentValid,
      isUserAddressValid,
      isCityNameValid,
      isUserZipCodeValid,
      isProvinceOrStateNameValid,
    } = this.state;

    const areFirstStepsFieldsValid =
      isIdentityDocumentValid &&
      isUserAddressValid &&
      isCityNameValid &&
      isUserZipCodeValid &&
      isProvinceOrStateNameValid &&
      isPhoneNumberValid;

    return areFirstStepsFieldsValid;
  };

  checkRequirementsToPay = () => {
    const {
      guestEmail,
      isGuestCheckout,
      selectedPaymentMethod,
      countryPrefix,
    } = this.context;
    const isEbanxCard =
      selectedPaymentMethod.title === METHODS.EBANX_CARD ||
      selectedPaymentMethod.title === METHODS.EBANX_CARD_AR;
    const {
      isCreditCardNumberValid,
      isCreditCardCVVValid,
      isCreditCardExpiryValid,
      isFullNameValid,
      isPhoneNumberValid,
      isIdentityDocumentValid,
      isUserAddressValid,
      isCityNameValid,
      isUserZipCodeValid,
      isProvinceOrStateNameValid,
      checkedTerms,
    } = this.state;

    const isCreditCardValid =
      isCreditCardNumberValid &&
      isCreditCardExpiryValid &&
      isCreditCardCVVValid &&
      checkedTerms;

    let isPaymentButtonActive = isCreditCardValid;

    if (selectedPaymentMethod.title === METHODS.CONEKTA_CARD) {
      isPaymentButtonActive =
        isCreditCardValid && isFullNameValid && isPhoneNumberValid;
    } else if (isEbanxCard) {
      isPaymentButtonActive =
        isCreditCardValid &&
        isFullNameValid &&
        isIdentityDocumentValid &&
        isUserAddressValid &&
        isCityNameValid &&
        isUserZipCodeValid &&
        isProvinceOrStateNameValid &&
        isPhoneNumberValid;
    } else if (
      selectedPaymentMethod.title === METHODS.STRIPE &&
      countryPrefix === 'co'
    ) {
      isPaymentButtonActive = isCreditCardValid && isIdentityDocumentValid;
    }
    if (isGuestCheckout) {
      isPaymentButtonActive =
        isPaymentButtonActive && guestEmail && guestEmail.isEmailValid;
    }

    return isPaymentButtonActive;
  };

  clearTextError = () => {
    this.setState({
      creditCardNumberErrorMessage: '',
      creditCardExpiryErrorMessage: '',
      creditCardCVVErrorMessage: '',
      paymentError: '',
    });
  };

  returnErrorMessage = (code: string) => {
    switch (code) {
      case 'incorrect_cvc':
        this.setState({
          creditCardCVVErrorMessage: I18N_STRINGS.INVALID_CVC_CVV,
          isCreditCardCVVValid: false,
        });
        break;
      case 'invalid_number':
        this.setState({
          creditCardNumberErrorMessage: I18N_STRINGS.INVALID_CARD_NUMBER,
          isCreditCardNumberValid: false,
          creditCardNumberFocus: true,
        });
        break;
      case 'incorrect_number':
        this.setState({
          creditCardNumberErrorMessage: I18N_STRINGS.INCORRECT_CARD_NUMBER,
          isCreditCardNumberValid: false,
          creditCardNumberFocus: true,
        });
        break;
      case 'expired_card':
        this.setState({
          creditCardNumberErrorMessage: I18N_STRINGS.EXPIRED_CARD,
          isCreditCardNumberValid: false,
          creditCardNumberFocus: true,
        });
        break;
      case 'card_declined':
        this.setState({
          creditCardNumberErrorMessage: I18N_STRINGS.CARD_DECLINED,
          isCreditCardNumberValid: false,
          creditCardNumberFocus: true,
        });
        break;
      case 'processing_error':
        this.setState({
          paymentError: I18N_STRINGS.CATCH_MESSAGE,
          creditCardNumberFocus: true,
        });
        break;
      case 'invalid_expiry_year':
        this.setState({
          creditCardExpiryErrorMessage: I18N_STRINGS.INVALID_EXPIRATION_DATE,
          creditCardExpiryFocus: true,
        });
        break;
      case 'approve_with_id':
        this.setState({
          paymentError: I18N_STRINGS.INSUFFICIENT_FUNDS,
        });
        break;
      case 'withdrawal_count_limit_exceeded':
        this.setState({
          paymentError: I18N_STRINGS.INSUFFICIENT_FUNDS,
        });
        break;
      default:
        this.setState({
          paymentError: I18N_STRINGS.DEFAULT_PAYMENT_ERROR,
        });
    }
  };

  verifyPublicKeys = () => {
    this.setState({ processing: false });
    this.setState({
      paymentError: I18N_STRINGS.PUBLIC_KEY_NO_FOUND,
    });
  };

  onClickPay = () => {
    this.clearTextError();

    const { creditCardNumber, creditCardExpiry, creditCardCVV } = this.state;

    this.setState({ processing: true });
    window.Stripe.card.createToken(
      {
        number: creditCardNumber,
        exp: creditCardExpiry,
        cvc: creditCardCVV,
      },
      this.stripeResponseHandler,
    );
  };

  stripeResponseHandler = (status: number, res: StripeTokenResponse) => {
    const { CONEKTA_PUBLIC_KEY, EBANX_PUBLIC_INTEGRATION_KEY } = window;

    const { guestEmail, selectedPaymentMethod, countryPrefix } = this.context;

    const { checkoutProductDetail } = this.context;
    const {
      creditCardNumber,
      fullName,
      creditCardExpiry,
      creditCardCVV,
      checkedTerms,
    } = this.state;
    const isEbanxCard =
      selectedPaymentMethod.title === METHODS.EBANX_CARD ||
      selectedPaymentMethod.title === METHODS.EBANX_CARD_AR;

    if (res.error) {
      this.setState({ processing: false });
      if (res.error.type === 'invalid_request_error') {
        this.setState({
          creditCardNumberErrorMessage: I18N_STRINGS.REQUIRED_CARD_NUMBER,
          creditCardExpiryErrorMessage: I18N_STRINGS.REQUIRED_EXPIRATION_DATE,
          creditCardCVVErrorMessage: I18N_STRINGS.REQUIRED_CVC_CVV,
        });
      } else {
        this.returnErrorMessage(res.error.code);
      }
    } else if (
      selectedPaymentMethod.title === METHODS.STRIPE_MEXICO &&
      status === 200
    ) {
      // payment with Stripe México
      // @ts-expect-error
      if (this.props.isStripeMexico) {
        this.setState({ stripeToken: res.id });
        window.Stripe.card.createToken(
          {
            number: creditCardNumber,
            exp: creditCardExpiry,
            cvc: creditCardCVV,
          },
          this.stripeMexicoResponseHandler,
        );
      } else {
        this.verifyPublicKeys();
      }
    } else if (selectedPaymentMethod.title === METHODS.CONEKTA_CARD) {
      if (CONEKTA_PUBLIC_KEY) {
        this.setState({ stripeToken: res.id });
        window.Conekta.setPublicKey(CONEKTA_PUBLIC_KEY);
        const dateString = creditCardExpiry.split('/');

        const expMonth = dateString[0];
        const expYear = dateString[1];

        const card = {
          number: creditCardNumber,
          name: fullName,
          exp_month: expMonth,
          exp_year: `20${expYear}`,
          cvc: creditCardCVV,
        };

        window.Conekta.Token.create(
          { card },
          this.successResponseHandlerConekta,
        );
      } else {
        this.verifyPublicKeys();
      }
    } else if (isEbanxCard) {
      if (EBANX_PUBLIC_INTEGRATION_KEY) {
        this.setState({ stripeToken: res.id });

        // TODO: Next step.- replace window.EBANX with useEbanxLib
        if (window.EBANX_DEBUG === 'True') {
          window.EBANX.config.setMode('test');
        } else {
          window.EBANX.config.setMode('production');
        }
        window.EBANX.config.setPublishableKey(EBANX_PUBLIC_INTEGRATION_KEY);
        window.EBANX.config.setCountry(countryPrefix);
        const dateString = creditCardExpiry.split('/');

        const expMonth = dateString[0];
        const expYear = dateString[1];

        const card = {
          card_number: creditCardNumber.replace(/\s+/g, ''),
          card_name: fullName,
          card_due_date: `${expMonth}/20${expYear}`,
          card_cvv: creditCardCVV,
        };

        window.EBANX.card.createToken(card, this.ebanxHandlerResponse);
      } else {
        this.verifyPublicKeys();
      }
    } else {
      const variables = {
        paymentGateway: selectedPaymentMethod.paymentGatewayId,
        itemType: checkoutProductDetail.type,
        itemId: checkoutProductDetail.originalId,
        stripeSafeCard: checkedTerms,
        stripeToken: res.id,
        stripeTokenMexico: '',
        couponCode: checkoutProductDetail.couponCode,
        giftcard: checkoutProductDetail.giftcard,
        email: guestEmail ? guestEmail.email : '',
        membershipStartedDate: checkoutProductDetail.membershipStartedDate,
        // @ts-expect-error
        quotaNumber: this.props.selectedQuotaNumber,
      };

      this.setPayment(variables);
    }
  };

  ebanxHandlerResponse = (response: EbanxTokenResponse) => {
    if (response.data && response.data.status) {
      const { checkoutProductDetail, selectedPaymentMethod } = this.context;
      const {
        deviceId,
        token,
        masked_card_number: maskedCardNumber,
      } = response.data;
      const {
        stripeToken,
        phoneNumber,
        fullName,
        cityName,
        userAddress,
        userZipCode,
        provinceOrStateName,
        identityDocument,
        checkedNewsLetterTerms,
      } = this.state;
      const { guestEmail } = this.context;
      const variables = {
        paymentGateway: selectedPaymentMethod.paymentGatewayId,
        itemType: checkoutProductDetail.type,
        itemId: checkoutProductDetail.originalId,
        stripeSafeCard: false,
        stripeToken,
        stripeTokenMexico: '',
        conektaToken: '',
        ebanxToken: token,
        couponCode: checkoutProductDetail.couponCode,
        giftcard: checkoutProductDetail.giftcard,
        email: guestEmail ? guestEmail.email : '',
        phoneNumber,
        fullName,
        city: cityName,
        zipCode: userZipCode,
        state:
          // @ts-expect-error
          provinceOrStateName && provinceOrStateName.value
            ? // @ts-expect-error
              provinceOrStateName.value
            : provinceOrStateName,
        deviceId,
        creditCardNumber: maskedCardNumber,
        address: userAddress,
        document: identityDocument,
        membershipStartedDate: checkoutProductDetail.membershipStartedDate,
        // @ts-expect-error
        quotaNumber: this.props.selectedQuotaNumber,
        checkedNewsLetterTerms,
      };

      this.setPayment(variables);
    } else {
      this.setState({ processing: false });
      this.setState({
        paymentError:
          // @ts-expect-error
          response?.error?.err?.status_message || response?.error?.err?.message,
      });
    }
  };

  successResponseHandlerConekta = (token: { id: string }) => {
    if (token) {
      const { stripeToken, phoneNumber, fullName } = this.state;
      const { guestEmail, checkoutProductDetail, selectedPaymentMethod } =
        this.context;

      const variables = {
        paymentGateway: selectedPaymentMethod.paymentGatewayId,
        itemType: checkoutProductDetail.type,
        itemId: checkoutProductDetail.originalId,
        stripeSafeCard: false,
        stripeToken,
        stripeTokenMexico: '',
        conektaToken: token.id,
        couponCode: checkoutProductDetail.couponCode,
        giftcard: checkoutProductDetail.giftcard,
        email: guestEmail ? guestEmail.email : '',
        phoneNumber,
        fullName,
        membershipStartedDate: checkoutProductDetail.membershipStartedDate,
        // @ts-expect-error
        quotaNumber: this.props.selectedQuotaNumber,
      };

      this.setPayment(variables);
    }
  };

  // @ts-expect-error
  stripeMexicoResponseHandler = (status, res) => {
    const { guestEmail, checkoutProductDetail, selectedPaymentMethod } =
      this.context;

    const { stripeToken, checkedTerms } = this.state;

    if (res.error && res.error.message !== 'Your card is not supported.') {
      this.setState({ processing: false });
      if (res.error.type === 'invalid_request_error') {
        this.setState({
          creditCardNumberErrorMessage: I18N_STRINGS.REQUIRED_CARD_NUMBER,
          creditCardExpiryErrorMessage: I18N_STRINGS.REQUIRED_EXPIRATION_DATE,
          creditCardCVVErrorMessage: I18N_STRINGS.REQUIRED_CVC_CVV,
        });
      } else {
        this.returnErrorMessage(res.error.code);
      }
    } else {
      const stripeTokenMexico =
        res.error && res.error.message === 'Your card is not supported.'
          ? ''
          : res.id;

      const variables = {
        paymentGateway: selectedPaymentMethod.paymentGatewayId,
        itemType: checkoutProductDetail.type,
        itemId: checkoutProductDetail.originalId,
        stripeSafeCard: checkedTerms,
        stripeToken,
        stripeTokenMexico,
        couponCode: checkoutProductDetail.couponCode,
        giftcard: checkoutProductDetail.giftcard,
        email: guestEmail ? guestEmail.email : '',
        membershipStartedDate: checkoutProductDetail.membershipStartedDate,
        // @ts-expect-error
        quotaNumber: this.props.selectedQuotaNumber,
      };

      this.setPayment(variables);
    }
  };

  // @ts-expect-error
  setPayment = variables => {
    const {
      checkoutProductDetail,
      selectedPaymentMethod,
      buyNowMutation,
      countryPrefix,
    } = this.context;

    variables.itemPromotionToken = checkoutProductDetail.promotionToken;
    variables.paymentSource = PaymentSources.CHECKOUT_VIEW;
    if (countryPrefix === CountryPrefixEnum.COLOMBIA) {
      const { identityDocument, documentType } = this.state;

      variables.documentType = documentType;
      variables.document = identityDocument;
    }

    buyNowMutation({ variables })
      // @ts-expect-error
      .then(payment => {
        if (payment) {
          const { success, redirectUrl, messages } = payment;

          const mutationStatusOrErrorCode =
            messages && messages.length > 0 ? messages[0].message : null;

          this.setState({ paymentMutationStatus: mutationStatusOrErrorCode });

          if (success && redirectUrl) {
            window.location.href = redirectUrl;
          } else {
            this.returnErrorMessage(mutationStatusOrErrorCode);
          }
        }

        this.setState({
          processing: false,
        });
      })
      // @ts-expect-error
      .catch(err => {
        this.setState({
          paymentError: I18N_STRINGS.CATCH_MESSAGE,
          processing: false,
        });
        // @ts-expect-error
        window.Sentry?.withScope(scope => {
          scope.setTag('paymentMethod', selectedPaymentMethod.title);
          window.Sentry.captureException(err);
        });
      });
  };

  // @ts-expect-error
  verifyBINPartnershipMutation = ({ bin, itemId }) => {
    const { client } = this.props;

    // @ts-expect-error
    client
      .mutate({
        mutation: VERIFY_BIN_PARTNERSHIP_MUTATION,
        variables: {
          bin,
          itemId,
        },
        context: { clientName: 'payments' },
      })
      .then(res => {
        if (!res?.data?.verifyBinPartnership?.success) {
          const creditCardNumberErrorMessage =
            res.data.verifyBinPartnership?.response?.message || '';

          this.setState({
            isCreditCardNumberValid: false,
            creditCardNumberErrorMessage,
          });
        }
      })
      .catch(() => {
        this.setState({
          isCreditCardNumberValid: false,
          creditCardNumberErrorMessage: I18N_STRINGS.ERROR_CARD_BIN,
        });
      });
  };

  setCheckedTerms = (checked: boolean) => {
    const { setPaymentFormValues } = this.context;

    this.setState({ checkedTerms: checked });
    setPaymentFormValues({ termsAndConditions: { isComplete: checked } });
  };

  setCheckedNewsLetterTerms = (checked: boolean) => {
    const { setPaymentFormValues } = this.context;
    this.setState({ checkedNewsLetterTerms: checked });
    setPaymentFormValues({
      checkedNewsLetterTerms: { isComplete: true, value: checked },
    });
  };

  render() {
    const {
      processing,
      documentType,
      paymentMutationStatus,
      checkedTerms,
      checkedNewsLetterTerms,
      ...restState
    } = this.state;
    const {
      onEmailChange,
      handleOnblurEmail,
      guestEmail,
      loadingItem,
      selectedPaymentMethod,
      checkoutProductDetail: { type },
      user,
    } = this.context;

    // @ts-expect-error
    const { togglePaymentWithSavedCard, hasSavedCard, ...restprops } =
      this.props;

    const isPaymentButtonActive = processing || this.checkRequirementsToPay();
    const inputsLength =
      inputValues[selectedPaymentMethod.title] || inputValues.default;

    if (loadingItem) {
      return (
        <div className="flex justify-center w-full m-24">
          <Loader size={48} />
        </div>
      );
    }
    if (paymentMutationStatus === 'pending') {
      return <PendingOnlinePayment />;
    }
    const isEbanxCardArg =
      selectedPaymentMethod?.title === METHODS.EBANX_CARD_AR;
    const isEbanxCard =
      selectedPaymentMethod?.title === METHODS.EBANX_CARD || isEbanxCardArg;

    const userEmail = guestEmail?.email || user?.email;
    const canOpenFailedPopup =
      customLocalStorage.getItem('failedPayment') !== 'saved' &&
      (paymentMutationStatus === 'card_declined' ||
        paymentMutationStatus === 'processing_error');

    return (
      <div className="flex flex-col w-full">
        <Form
          onChangeCreditCardNumber={this.onChangeCreditCardNumber}
          onBlurCreditCardNumber={this.onBlurCreditCardNumber}
          onChangeFullName={this.onChangeFullName}
          onBlurFullName={this.onBlurFullName}
          onChangeCreditCardCVV={this.onChangeCreditCardCVV}
          onBlurCVV={this.onBlurCVV}
          onChageCreditCardExpiry={this.onChageCreditCardExpiry}
          onBlurCreditCardExpiry={this.onBlurCreditCardExpiry}
          onChangeIdentityDocument={this.onChangeIdentityDocument}
          onChangeDocumentType={this.onChangeDocumentType}
          documentType={documentType}
          onBlurIdentityDocument={this.onBlurIdentityDocument}
          onChangeUserAddress={this.onChangeUserAddress}
          onBlurUserAddress={this.onBlurUserAddress}
          onChangeCityName={this.onChangeCityName}
          onBlurCityName={this.onBlurCityName}
          onChangeUserZipCode={this.onChangeUserZipCode}
          onBlurUserZipCode={this.onBlurUserZipCode}
          onChangeProvinceOrStateName={this.onChangeProvinceOrStateName}
          onBlurProvinceOrStateName={this.onBlurProvinceOrStateName}
          onChangePhoneNumber={this.onChangePhoneNumber}
          onBlurPhoneNumber={this.onBlurPhoneNumber}
          checkRequirementsToPay={this.checkRequirementsToPay}
          onClickPay={this.onClickPay}
          isPaymentButtonActive={isPaymentButtonActive}
          onEmailChange={onEmailChange}
          handleOnblurEmail={handleOnblurEmail}
          guestEmail={guestEmail}
          areFirstStepsFieldsValid={this.validateFirstStepsFields()}
          isMembership={type === 'membership'}
          checkedTerms={checkedTerms}
          setCheckedTerms={this.setCheckedTerms}
          checkedNewsLetterTerms={checkedNewsLetterTerms}
          setCheckedNewsLetterTerms={this.setCheckedNewsLetterTerms}
          brazilState={brazilState}
          isEbanxCardArg={isEbanxCardArg}
          isEbanxCard={isEbanxCard}
          isProcessing={processing}
          togglePaymentWithSavedCard={togglePaymentWithSavedCard}
          hasSavedCard={hasSavedCard}
          {...restState}
          {...restprops}
          {...inputsLength}
        />
        <FailedPaymentPopup email={userEmail} open={canOpenFailedPopup} />
      </div>
    );
  }
}

CheckoutForm.contextType = CheckoutContext;

export default withApollo(CheckoutForm);
